// Wire-shaped Live Updates fixtures mirroring the nexus PR #870 contract.
// Shared by unit tests and the Playwright e2e fixture server so both exercise
// the exact response shapes the sandbox merchant API serves.
import {
  LiveUpdatesConfigurationDetailWire,
  LiveUpdatesConfigurationsWire,
  LiveUpdatesLocationsWire,
  LiveUpdatesStatusKey,
  LiveUpdatesStatusWire,
  LiveUpdatesSummaryWire,
} from 'api/liveUpdates.types'

export const TIMER_OFFSET_VALUES = [0, 5, 10, 15, 20, 25, 30]

type StatusSeed = {
  status: LiveUpdatesStatusKey
  stage: string
  section: LiveUpdatesStatusWire['section']
  required: boolean
  enabled: boolean
  channels: LiveUpdatesStatusWire['channels']
  timerEligible: boolean
  triggerMode: LiveUpdatesStatusWire['trigger_mode']
  timerOffset: number | null
  timerDirection: LiveUpdatesStatusWire['timer_direction']
  deliveryOnly: boolean
}

// Mirrors nucleus OrderStatus::StatusConfiguration::STATUS_DEFAULTS plus the
// per-status channel map from LiveUpdates::StatusPresenter.
const STATUS_SEEDS: StatusSeed[] = [
  {
    status: 'placed',
    stage: 'Placed',
    section: 'shared',
    required: true,
    enabled: true,
    channels: ['live_activity'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: false,
  },
  {
    status: 'preparing',
    stage: 'Preparing',
    section: 'shared',
    required: false,
    enabled: false,
    channels: ['live_activity'],
    timerEligible: true,
    triggerMode: 'timer',
    timerOffset: 10,
    timerDirection: 'before',
    deliveryOnly: false,
  },
  {
    status: 'ready',
    stage: 'Ready',
    section: 'pickup',
    required: false,
    enabled: true,
    channels: ['live_activity'],
    timerEligible: true,
    triggerMode: 'timer',
    timerOffset: 10,
    timerDirection: 'after',
    deliveryOnly: false,
  },
  {
    status: 'picked_up',
    stage: 'On the way',
    section: 'delivery',
    required: false,
    enabled: true,
    channels: ['live_activity'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: true,
  },
  {
    status: 'en_route',
    stage: 'On the way',
    section: 'delivery',
    required: false,
    enabled: true,
    channels: ['live_activity'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: true,
  },
  {
    status: 'arrived',
    stage: 'On the way',
    section: 'delivery',
    required: false,
    enabled: true,
    channels: ['live_activity'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: true,
  },
  {
    status: 'delivered',
    stage: 'Delivered',
    section: 'delivery',
    required: false,
    enabled: true,
    channels: ['live_activity'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: true,
  },
  {
    status: 'canceled_by_restaurant',
    stage: 'Canceled',
    section: 'canceled',
    // FR-47: cancellation cards carry a Disable link (merchant can turn the
    // push off), so — unlike `placed` — these are NOT required/always-on.
    required: false,
    enabled: true,
    channels: ['push'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: false,
  },
  {
    status: 'canceled_by_delivery',
    stage: 'Canceled',
    section: 'canceled',
    // FR-47: same disable-allowed rule as canceled_by_restaurant above.
    required: false,
    enabled: true,
    channels: ['push'],
    timerEligible: false,
    triggerMode: 'event',
    timerOffset: null,
    timerDirection: null,
    deliveryOnly: true,
  },
]

// Thanx read-time default copy per status, mirroring nucleus
// OrderStatus::StatusConfiguration::DEFAULT_COPY exactly (the companion nexus PR
// emits these on each content field's `default` key). `preparing` is in-app only
// and never fires a push, so its push default is null.
type StatusDefaultCopy = {
  status_title: string
  push_message_text: string | null
  homescreen_text: string
}

const DEFAULT_COPY: Record<LiveUpdatesStatusKey, StatusDefaultCopy> = {
  placed: {
    status_title: 'Placed',
    push_message_text: null,
    homescreen_text: "We've received your order",
  },
  preparing: {
    status_title: 'Preparing',
    push_message_text: null,
    homescreen_text: "We're preparing your order",
  },
  ready: {
    status_title: 'Ready',
    push_message_text: null,
    homescreen_text: 'Your order should be ready soon',
  },
  picked_up: {
    status_title: 'Picked up',
    push_message_text: null,
    homescreen_text: 'Your order has been picked up',
  },
  en_route: {
    status_title: 'On the way',
    push_message_text: null,
    homescreen_text: 'Your order is on the way',
  },
  arrived: {
    status_title: 'Arrived',
    push_message_text: null,
    homescreen_text: 'Your driver has arrived',
  },
  delivered: {
    status_title: 'Delivered',
    push_message_text: null,
    homescreen_text: 'Your order has been delivered',
  },
  canceled_by_restaurant: {
    status_title: 'Canceled',
    push_message_text: 'Your order has been canceled',
    homescreen_text: 'Your order has been canceled',
  },
  canceled_by_delivery: {
    status_title: 'Canceled',
    push_message_text: 'Your order has been canceled',
    homescreen_text: 'Your order has been canceled',
  },
}

type BuildStatusOptions = {
  deliveryConfigurable?: boolean
  configurable?: boolean
  pushManagedByFlybuy?: boolean
  triggerLabel?: string
  // FR-47: the delivery-triggered cancellation carries a distinct badge from
  // the restaurant one ("TRIGGERED BY OLO DISPATCH" vs "TRIGGERED BY OLO" in
  // Figma). Defaults to `triggerLabel` so stacks that never render this card
  // (no delivery provider) are unaffected.
  canceledByDeliveryTriggerLabel?: string
  overrides?: Partial<
    Record<LiveUpdatesStatusKey, Partial<LiveUpdatesStatusWire>>
  >
}

export function buildStatusesWire(
  options: BuildStatusOptions = {}
): LiveUpdatesStatusWire[] {
  const {
    deliveryConfigurable = true,
    configurable = true,
    pushManagedByFlybuy = false,
    triggerLabel = 'Triggered by Olo',
    canceledByDeliveryTriggerLabel = triggerLabel,
    overrides = {},
  } = options

  return STATUS_SEEDS.map(seed => {
    const applicable = seed.deliveryOnly ? deliveryConfigurable : true
    const editable = configurable && applicable
    // Event-triggered cancellations are provider-specific: the delivery one
    // reads the delivery provider's badge, everything else the ordering label.
    const eventTriggerLabel =
      seed.status === 'canceled_by_delivery'
        ? canceledByDeliveryTriggerLabel
        : triggerLabel
    // Mirrors nucleus's PUSH_ELIGIBLE_STATUSES: only a status that still
    // carries the push channel gets a push_message_text key at all — everyone
    // else has it omitted entirely, matching the real wire contract.
    const pushEligible = seed.channels.includes('push')
    const status: LiveUpdatesStatusWire = {
      status: seed.status,
      stage: seed.stage,
      section: seed.section,
      applicable,
      required: seed.required,
      enabled: seed.enabled,
      source: 'default',
      channels: seed.channels,
      trigger_source: {
        label:
          seed.triggerMode === 'timer'
            ? 'Based on est. ready time'
            : eventTriggerLabel,
        detail: null,
      },
      timer_eligible: seed.timerEligible,
      event_supported: true,
      trigger_mode: seed.triggerMode,
      timer_offset: seed.timerOffset,
      timer_offset_values: TIMER_OFFSET_VALUES,
      timer_direction: seed.timerDirection,
      content: {
        status_title: {
          value: null,
          max_length: 35,
          editable,
          default: DEFAULT_COPY[seed.status].status_title,
        },
        ...(pushEligible
          ? {
              push_message_text: {
                value: null,
                max_length: 150,
                editable: editable && !pushManagedByFlybuy,
                default: DEFAULT_COPY[seed.status].push_message_text,
              },
            }
          : {}),
        homescreen_text: {
          value: null,
          max_length: 45,
          editable,
          default: DEFAULT_COPY[seed.status].homescreen_text,
        },
      },
      ...overrides[seed.status],
    }
    return status
  })
}

export const summaryFixture: LiveUpdatesSummaryWire = {
  summary: {
    eligible: true,
    configurations_count: 3,
    flybuy: true,
    warnings: [],
  },
}

// AI-705 dropped POS from the stack (and its name) entirely, so no label
// below names a POS provider (e.g. Toast, Square) even where the row still
// carries a POS-era id. label never carries a count suffix — that's composed
// client-side from the separate locations_count field.
const toastOloRow = {
  id: 'toastolo1234567',
  label: 'Olo + Olo Dispatch',
  stack: {
    ordering_provider: { key: 'olo', display_name: 'Olo' },
    delivery_provider: { key: 'olo', display_name: 'Olo Dispatch' },
    status_provider: null,
  },
  locations_count: 125,
  configurable: true,
  delivery_configurable: true,
  customized: false,
  last_published_at: null,
  refreshed_at: '2026-07-14T18:22:00Z',
}

// Flybuy-standardized stacks never populate `delivery_provider` (Flybuy
// signatures don't carry a delivery_provider_key — nucleus's
// OrderStatus::Configuration#signature_for), but `delivery_configurable` is
// unconditionally true for them so merchants can configure Delivery-section
// messaging ahead of need (nexus thanx/nexus#1630).
const flybuyRow = {
  id: 'flybuygroup5678',
  label: 'Flybuy',
  stack: {
    ordering_provider: null,
    delivery_provider: null,
    status_provider: { key: 'flybuy', display_name: 'Flybuy' },
  },
  locations_count: 12,
  configurable: true,
  delivery_configurable: true,
  customized: true,
  last_published_at: '2026-07-10T02:11:00Z',
  refreshed_at: '2026-07-14T18:22:00Z',
}

const unsupportedRow = {
  id: 'unsupported9012',
  label: 'Unsupported',
  stack: {
    ordering_provider: { key: 'unsupported', display_name: 'Unsupported' },
    delivery_provider: null,
    status_provider: null,
  },
  locations_count: 3,
  configurable: false,
  delivery_configurable: false,
  customized: false,
  last_published_at: null,
  refreshed_at: '2026-07-14T18:22:00Z',
}

// FR-75: delivery is a self-managed (unsupported) integration — Thanx never
// receives delivery signals for these locations, so the delivery section
// renders the locked card (`sections.ts`'s `deliveryLockedMode` keys off the
// `self_managed` provider sentinel). Pickup/shared/canceled stay fully
// configurable.
const selfManagedRow = {
  id: 'selfmanaged3456',
  label: 'DoorDash Marketplace',
  stack: {
    ordering_provider: { key: 'doordash', display_name: 'DoorDash' },
    delivery_provider: {
      key: 'self_managed',
      display_name: 'Self managed (unsupported)',
    },
    status_provider: null,
  },
  locations_count: 8,
  configurable: true,
  delivery_configurable: false,
  customized: false,
  last_published_at: null,
  refreshed_at: '2026-07-14T18:22:00Z',
}

// FR-75: delivery isn't an enabled handoff mode for these locations (pickup
// only) — a real delivery provider exists on the stack, but `deliveryLockedMode`
// falls back to `disabled` because `delivery_configurable` is false.
const deliveryDisabledRow = {
  id: 'deliverydisabled789',
  label: 'Uptown Pickup Only',
  stack: {
    ordering_provider: { key: 'olo', display_name: 'Olo' },
    delivery_provider: { key: 'olo', display_name: 'Olo Dispatch' },
    status_provider: null,
  },
  locations_count: 4,
  configurable: true,
  delivery_configurable: false,
  customized: false,
  last_published_at: null,
  refreshed_at: '2026-07-14T18:22:00Z',
}

// FR-03: every optional stage disabled (preparing off by default; ready +
// every delivery stage explicitly disabled below) — only the always-on
// `placed` stage (plus the two cancellation pushes) remains. Drives the
// confirmed-only Live Activity / in-app tracker treatment.
const confirmedOnlyRow = {
  id: 'confirmedonly012',
  label: 'Confirmed-Only Cafe',
  stack: {
    ordering_provider: { key: 'olo', display_name: 'Olo' },
    delivery_provider: { key: 'olo', display_name: 'Olo Dispatch' },
    status_provider: null,
  },
  locations_count: 2,
  configurable: true,
  delivery_configurable: true,
  customized: false,
  last_published_at: null,
  refreshed_at: '2026-07-14T18:22:00Z',
}

// NOTE: intentionally still just these three rows — src/api/liveUpdates.test.ts
// asserts on this list by exact length and position. The self-managed,
// delivery-disabled, and confirmed-only rows above are exposed only via their
// *DetailFixture exports below; e2e/mockApi.ts (the Playwright picker needs a
// `configurations` list entry to switch to them) layers them on top of this
// fixture rather than this file growing the list itself.
export const configurationsFixture: LiveUpdatesConfigurationsWire = {
  configurations: [toastOloRow, flybuyRow, unsupportedRow],
  unassigned_locations_count: 2,
}

export const detailFixture: LiveUpdatesConfigurationDetailWire = {
  configuration: {
    ...toastOloRow,
    current_version: 0,
    // FR-47: the delivery cancellation shows the delivery provider's badge
    // (Olo Dispatch), distinct from the restaurant one (Olo).
    statuses: buildStatusesWire({
      canceledByDeliveryTriggerLabel: 'Triggered by Olo Dispatch',
    }),
  },
}

export const flybuyDetailFixture: LiveUpdatesConfigurationDetailWire = {
  configuration: {
    ...flybuyRow,
    current_version: 2,
    statuses: buildStatusesWire({
      deliveryConfigurable: true,
      pushManagedByFlybuy: true,
      triggerLabel: 'Triggered by Flybuy',
    }),
  },
}

export const unsupportedDetailFixture: LiveUpdatesConfigurationDetailWire = {
  configuration: {
    ...unsupportedRow,
    current_version: 0,
    statuses: buildStatusesWire({
      configurable: false,
      deliveryConfigurable: false,
      triggerLabel: 'Triggered by Square',
    }),
  },
}

// FR-75 (self-managed): delivery statuses are `applicable: false` (Thanx can't
// send them), so `sectionForStatuses` moves picked_up/en_route/arrived/
// delivered/canceled_by_delivery into the delivery section's suppressed list.
export const selfManagedDetailFixture: LiveUpdatesConfigurationDetailWire = {
  configuration: {
    ...selfManagedRow,
    current_version: 0,
    statuses: buildStatusesWire({
      deliveryConfigurable: false,
      triggerLabel: 'Triggered by DoorDash',
    }),
  },
}

// FR-75 (disabled): same suppressed-list treatment as self-managed, driven by
// `delivery_configurable: false` on an otherwise-normal delivery provider.
export const deliveryDisabledDetailFixture: LiveUpdatesConfigurationDetailWire =
  {
    configuration: {
      ...deliveryDisabledRow,
      current_version: 0,
      statuses: buildStatusesWire({ deliveryConfigurable: false }),
    },
  }

// FR-03: ready + every delivery stage disabled (preparing is already disabled
// by the default seed) — only `placed` (plus cancellations) remains enabled.
export const confirmedOnlyDetailFixture: LiveUpdatesConfigurationDetailWire = {
  configuration: {
    ...confirmedOnlyRow,
    current_version: 0,
    statuses: buildStatusesWire({
      canceledByDeliveryTriggerLabel: 'Triggered by Olo Dispatch',
      overrides: {
        ready: { enabled: false },
        picked_up: { enabled: false },
        en_route: { enabled: false },
        arrived: { enabled: false },
        delivered: { enabled: false },
      },
    }),
  },
}

export const locationsFixture: LiveUpdatesLocationsWire = {
  locations: [
    {
      id: 'loc1aaaaaaaaaaa',
      name: 'West Caldwell, NJ',
      street: '749 Bloomfield Ave Unit B',
      city: 'West Caldwell',
      state: 'NJ',
      zip: '07006',
    },
    {
      id: 'loc2bbbbbbbbbbb',
      name: 'Woodbridge, NJ',
      street: '869 Saint George Ave',
      city: 'Woodbridge',
      state: 'NJ',
      zip: '07095',
    },
    {
      id: 'loc3ccccccccccc',
      name: 'South Plainfield, NJ',
      street: '4919 Stelton Road',
      city: 'South Plainfield',
      state: 'NJ',
      zip: '07080',
    },
  ],
  pagination: {
    total_pages: 1,
    per_page: 50,
    current_page: 1,
    total_count: 3,
  },
}
